import { useId, useState, type ComponentProps } from "react";
import { format, isValid, parseISO } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

type DateTimeInputProps = Omit<ComponentProps<typeof Input>, "type" | "value" | "defaultValue" | "onChange"> & {
  value: string;
  onValueChange: (value: string) => void;
  label: string;
  timezone: string;
};

export function DateTimeInput({ className, value, onValueChange, label, timezone, ...props }: DateTimeInputProps) {
  const [open, setOpen] = useState(false);
  const [draftTime, setDraftTime] = useState<string>();
  const timeId = useId();
  const parsed = parseISO(value);
  const selected = isValid(parsed) ? parsed : undefined;
  const time = draftTime ?? (selected ? format(selected, "HH:mm") : "00:00");

  return (
    <Popover open={open} onOpenChange={(nextOpen) => { setOpen(nextOpen); setDraftTime(undefined); }}>
      <PopoverAnchor asChild>
        <div className={cn("relative", className)}>
          <Input
            {...props}
            value={value}
            onChange={(event) => { setDraftTime(undefined); onValueChange(event.target.value); }}
            type="datetime-local"
            className="pr-10 tabular-nums [&::-webkit-calendar-picker-indicator]:hidden"
          />
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={props.disabled}
              aria-label={`Choose ${label.toLowerCase()}`}
              className="absolute right-1 top-1/2 size-7 -translate-y-1/2 text-muted-foreground"
            >
              <CalendarIcon aria-hidden="true" />
            </Button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent className="w-auto max-h-(--radix-popover-content-available-height) overflow-y-auto p-0" align="start" aria-label={label}>
        <div className="border-b px-4 py-3 text-sm font-medium">{label}</div>
        <Calendar
          mode="single"
          required
          defaultMonth={selected}
          selected={selected}
          onSelect={(day) => {
            onValueChange(`${format(day, "yyyy-MM-dd")}T${time || format(selected ?? day, "HH:mm")}`);
            setDraftTime(undefined);
          }}
          showOutsideDays={false}
          fixedWeeks
        />
        <div className="border-t p-4">
          <div className="mb-2 flex items-center justify-between gap-4 text-xs">
            <label htmlFor={timeId} className="font-medium">Time</label>
            <span className="text-muted-foreground">{timezone}</span>
          </div>
          <div className="flex gap-2">
            <Input
              id={timeId}
              type="time"
              step={60}
              value={time}
              disabled={!selected}
              aria-invalid={!time}
              aria-describedby={!time ? `${timeId}-error` : undefined}
              className="min-w-0 flex-1 tabular-nums [&::-webkit-calendar-picker-indicator]:hidden"
              onChange={(event) => {
                const nextTime = event.target.value;
                setDraftTime(nextTime);
                if (selected && nextTime) onValueChange(`${format(selected, "yyyy-MM-dd")}T${nextTime}`);
              }}
            />
            <Button type="button" variant="outline" disabled={!time} onClick={() => setOpen(false)}>Done</Button>
          </div>
          {!time && <p id={`${timeId}-error`} role="alert" className="mt-2 text-xs text-danger">Enter a time.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
